import vueAuthInstance from '@/services/auth.js'
import { mapGetters } from 'vuex'
import Papa from 'papaparse'
import ApiService from '@/common/api.service'
import Intercom from '@intercom/messenger-js-sdk'
import CryptoES from 'crypto-es'

export default {
  name: 'UtilsMixin',
  data() {
    return {
      tileProviders: this.$appConfig.maps.providers,
      tileProvidersReady: false,
      statusReversible: [
        'DELIVERED',
        'CANCELED',
        'TOBERETURNED',
        'RETURNED'
      ],
      distanceCoefficient: 1
    }
  },
  computed: {
    /**
     * Check for `super-admin` role on user profile
     * @return {Boolean}
     */
    isSuperAdmin() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1
    },
    /**
     * Check for `admin` role on user profile
     * @return {Boolean}
     */
    isAdmin() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.ADMIN) !== -1
    },
    /**
     * Check for `courier-admin` role on user profile
     * @return {Boolean}
     */
    isCourierAdmin() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.COURIER_ADMIN) !== -1
    },
    /**
     * Check for `fleet-admin` role on user profile
     * @return {Boolean}
     */
    isFleetAdmin() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.FLEET_ADMIN) !== -1
    },
    /**
     * Check for `fleet` role on user profile
     * @return {Boolean}
     */
    isFleet() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.FLEET) !== -1
    },
    /**
     * Check for `onboarding` role on user profile
     * @return {Boolean}
     */
    isOnboarding() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.ONBOARDING) !== -1
    },
    /**
     * Check for `super-admin` or `head-sales` roles on user profile
     * @return {Boolean}
     */
    isHeadSales() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.HEAD_SALES) !== -1
    },
    /**
     * Check for `super-admin` or `commercial` roles on user profile
     * @return {Boolean}
     */
    isCommercial() {
      return this.$store.getters.profile.roles.find(x => x.name === this.$roles.SUPER_ADMIN) ||
        this.$store.getters.profile.roles.find(x => x.name === this.$roles.COMMERCIAL)
    },
    /**
     * Check for `super-admin` or `accountant` roles on user profile
     * @return {Boolean}
     */
    isAccountant() {
      return this.$store.getters.profile.roles.find(x => x.name === this.$roles.SUPER_ADMIN) ||
        this.$store.getters.profile.roles.find(x => x.name === this.$roles.ACCOUNTANT)
    },
    /**
     * Check for `super-admin` or `accountant` roles on user profile
     * @return {Boolean}
     */
    isAccountApprover() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.ACCOUNT_APPROVER) !== -1
    },
    /**
     * Check for `operator-supervisor` role on user profile
     * @return {Boolean}
     */
    isOperatorSupervisor() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.OPERATOR_SUPERVISOR) !== -1
    },
    /**
     * Check for `operator` role on user profile
     * @return {Boolean}
     */
    isOperator() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.OPERATOR) !== -1
    },
    /**
     * Check for `super-admin` or `head-operations` roles on user profile
     * @return {Boolean}
     */
    isHeadOperations() {
      return this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.ADMIN) !== -1 ||
        this.$store.getters.profile.roles.findIndex(x => x.name === this.$roles.HEAD_OPERATIONS) !== -1
    },
    isInternational() {
      return this.$store.getters.isInternational
    },
    /**
     * Check if the user has the Auth token, fetch user profile and set Store data if needed, else forces logout
     * @return {Boolean}
     */
    isAuthenticated() {
      if (!this.$store.state.auth.uid && vueAuthInstance.getPayload()) {
        let uid = vueAuthInstance.getPayload() ? vueAuthInstance.getPayload()['sub'] : null
        this.$store.commit('setUid', uid)
      }
      if (!this.$store.state.auth.email && this.$store.state.auth.isAuthenticated) {
        this.$http.get(this.$appConfig.api.baseUrl + 'users/' + this.$store.state.auth.uid)
          .then(response => {
            if (response.ok) {
              response.json().then(json => {
                if ((['whEntryMonitor', 'whExitMonitor', 'toursControlMonitor', 'whIncidencesMonitor'].indexOf(this.$route.name) === -1) && json.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) === -1 && json.roles.findIndex(x => x.name === this.$roles.OPERATOR) === -1) {
                  console.log('OUT')
                  this.$http.post(this.$appConfig.api.baseUrl + 'logout')
                    .then(() => {
                      this.$store.dispatch('logout').then(() => {
                        window.location.href = window.location.origin
                      })
                    })
                } else {
                  ApiService.query('countries')
                    .then(response => {
                      window.countries = response.body.data
                    })
                  this.$store.commit('setEmail', json.email)

                  const forceSetCountry = json.company ? json.company.country_iso_code : json.country_iso_code

                  let payload = { profile: json }
                  this.$store.commit('setProfile', payload)
                  this.$store.commit('isInternational', json.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) !== -1 || json.roles.findIndex(x => x.name === this.$roles.INTERNATIONAL) !== -1)

                  // Initialize Intercom with user data
                  this.initializeIntercom()

                  if (json.roles.findIndex(x => x.name === this.$roles.SUPER_ADMIN) === -1 && !this.isInternational) {
                    let location = window.location.href
                    if (window.countries.find(x => x.iso_code === forceSetCountry).front_config.bypassCountryCheck && this.$cookies.get('bypassCountry')) {
                      this.$cookies.remove('bypassCountry')
                      window.location.href = location
                    }
                    if (window.countries.find(x => x.iso_code === forceSetCountry).front_config.bypassCountryCheck && this.$cookies.get('country') !== forceSetCountry) {
                      this.$frontConfig = window.countries.find(x => x.iso_code === forceSetCountry).front_config
                      this.$tz = this.$frontConfig.i18n.tz
                      this.$cookies.set('country', forceSetCountry, '0', '/')
                      window.country = forceSetCountry
                      this.$cookies.set('phonePrefix', this.$frontConfig.i18n.phonePrefix, '0', '/')
                      window.phonePrefix = this.$frontConfig.i18n.phonePrefix
                      this.$language.current = this.$frontConfig.i18n.lang
                      window.location.href = location
                    }
                  }
                }
              })
            }
          })
      }
      return this.$store.state.auth.isAuthenticated
    },
    weekdaysByAbbr() {
      return {
        sun: this.$gettext('Sunday'),
        mon: this.$gettext('Monday'),
        tue: this.$gettext('Tuesday'),
        wed: this.$gettext('Wednesday'),
        thu: this.$gettext('Thursday'),
        fri: this.$gettext('Friday'),
        sat: this.$gettext('Saturday')
      }
    },
    ...mapGetters([
      'eid',
      'isMoova',
      'universe'
    ])
  },
  methods: {
    /**
     * Post to API logout to kill the token, dispatch to the Store to clean local states and reload Origin
     */
    logout() {
      this.$http.post(this.$appConfig.api.baseUrl + 'logout')
        .then(() => {
          this.$cookies.remove('countryForced')
          this.$store.dispatch('logout').then(() => {
            window.location.href = window.location.origin
          })
        })
    },
    /**
     * Removes duplicated entries from an array
     * @param  {Array} array The array with duplicated entries
     * @return {Array}       Te array without duplicated entries
     */
    arrayUnique(array) {
      let a = array.concat()
      for (let i = 0; i < a.length; ++i) {
        for (let j = i + 1; j < a.length; ++j) {
          if (a[i] === a[j]) {
            a.splice(j--, 1)
          }
        }
      }
      return a
    },
    /**
     * Returns the diff between one given date and now or two dates in a human readable string
     * @param  {String|Date} from            The "from" date (valid string or Date object)
     * @param  {String|Date} [to=Date.now()] The "to" date (valid string or Date object)
     * @return {String}                 A string with the diff like "32d 15h 23m" (can be negative if "from" is in the future)
     */
    elapsedTime(from, to = Date.now()) {
      let diffMs = this.moment.utc(to).diff(this.moment.utc(from), 'milliseconds', true)
      let diffDays = Math.floor(diffMs / 86400000)
      let diffHrs = Math.floor((diffMs % 86400000) / 3600000)
      let diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000)

      // HACK for negative times offset
      diffDays = (diffMs < 0 ? -diffDays - 1 : diffDays)
      diffHrs = (diffMs < 0 ? -diffHrs - 1 : diffHrs)
      diffMins = (diffMs < 0 ? -diffMins : diffMins)

      return (diffMs < 0 ? '-' : '') + (diffDays !== 0 ? diffDays + 'd ' : '') + (diffHrs !== 0 ? diffHrs + 'h ' : '') + (diffMins !== 0 ? diffMins + 'm ' : '0m')
    },
    elapsedTimeRaw(from, to = Date.now()) {
      let diffMs = this.moment.utc(to).diff(this.moment.utc(from), 'milliseconds', true)

      return diffMs
    },
    humanFileSize(bytes, si = false, dp = 1) {
      const thresh = si ? 1000 : 1024

      if (Math.abs(bytes) < thresh) {
        return bytes + ' B'
      }

      const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
      let u = -1
      const r = 10 ** dp

      do {
        bytes /= thresh
        ++u
      } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

      return bytes.toFixed(dp) + ' ' + units[u]
    },
    /**
     * Transforms an object or array into a nested <ul>
     * @param  {Number|String|Object|Array}  item The item to transfrom
     * @return {String}      The nested <ul> HTML tag
     */
    isJson(item, excludeKey = null) {
      if (
        typeof item === 'number' ||
        typeof item === 'boolean' ||
        (typeof item === 'string' && item.match(/^\d+$/)) // could it be a VERY BIG integer as string? let's avoid Number.MAX_SAFE_INTEGER rounding problem
      ) {
        return item
      }
      item = typeof item !== 'string'
        ? JSON.stringify(item)
        : item
      try {
        item = JSON.parse(item)
      } catch (e) {
        return item
      }
      let itemOut
      if (typeof item === 'object' && item !== null) {
        itemOut = '<ul>'
        for (let p in item) {
          console.log(p, excludeKey, p !== excludeKey)

          if (p !== excludeKey) {
            if (item[p] !== null && item[p].length !== 0) {
              itemOut += '<li>' + p + ': ' + (p.indexOf('phone') === -1 && p.indexOf('serialNumber') === -1 && p.indexOf('serial_number') === -1 ? this.isJson(item[p], excludeKey) : item[p].toString()) + '</li>'
            } else if (item[p] !== null && Array.isArray(item)) {
              itemOut += '<li>' + p + ': ' + this.isJson(item[p], excludeKey) + '</li>'
            } else if (item[p] === null || item[p] === '') {
              itemOut += '<li>' + p + ': null</li>'
            }
          }
        }
        itemOut += '</ul>'
      } else {
        itemOut = item
      }

      return itemOut
    },
    /**
     * Copies to the Clipboard a string. Requires Toast to show copy result.
     * @param  {String} str         The string to copy to Clipboard
     * @param  {String} [type='id'] Optional. The element type you are copying to show the generic or specific result message
     */
    copyToClipboard(str, type = 'id') {
      const el = document.createElement('textarea')
      el.value = str
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      el.select()
      let selection = document.getSelection()
      let range = document.createRange()
      try {
        range.selectNode(el)
        selection.removeAllRanges()
        selection.addRange(range)
        document.execCommand('copy')
        selection.removeAllRanges()
        let mssg
        switch (type) {
          case 'id':
            mssg = this.$gettext('The ID has been copied to Clipboard')
            break
          case 'email':
            mssg = this.$gettext('The e-mail has been copied to Clipboard')
            break
          default:
            mssg = this.$gettextInterpolate(this.$gettext('The %{type} has been copied to Clipboard'), {type: type})
        }
        this.makeToast(this.$gettext('Notice'), mssg, 'success', false, 2000)
      } catch (e) {
        let mssg
        switch (type) {
          case 'id':
            mssg = this.$gettext('The ID could NOT be copied to Clipboard')
            break
          case 'email':
            mssg = this.$gettext('The e-mail could NOT be copied to Clipboard')
            break
          default:
            mssg = this.$gettextInterpolate(this.$gettext('The %{type} could NOT be copied to Clipboard'), {type: type})
        }
        this.makeToast(this.$gettext('Warning'), mssg, 'danger', false, 2000)
      }
      document.body.removeChild(el)
    },
    /**
     * Shows a Toast message on screen
     * @param  {String}  title                 The top title of the Toast box
     * @param  {String}  mssg                  The message to show
     * @param  {String}  [variant='info']      The Bootstrap variant for Toast colors
     * @param  {Boolean} [append=false]        Should be appended or prepended?
     * @param  {Number|False} [autoHideDelay=false] Time in ms before auto hide the Toast or false
     * @param  {Boolean} [solid=true]          Opacity style for the Toast
     */
    makeToast(title, mssg, variant = 'info', append = false, autoHideDelay = false, solid = true) {
      let options = {
        title: title,
        appendToast: append,
        variant: variant,
        solid: solid
      }
      if (autoHideDelay !== false) {
        options['autoHideDelay'] = autoHideDelay
      } else {
        options['noAutoHide'] = true
      }

      this.$bvToast.toast([mssg], options)
    },
    /**
     * Makes a GET request to an URL, waits for response and then auto download the response
     * @param  {Object} data Should contain `url` and `params` for the request, and `type` and `filename` for the download
     */
    downloadAsync(data) {
      this.$http.get(data.url, { params: data.params })
        .then(response => {
          let blob = new Blob([response.body], { type: data.type })
          if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, data.filename)
          } else {
            let link = document.createElement('a')
            if (link.download !== undefined) { // feature detection
              // Browsers that support HTML5 download attribute
              let url = URL.createObjectURL(blob)
              link.setAttribute('href', url)
              link.setAttribute('download', data.filename)
              link.style.visibility = 'hidden'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }
          }
        }, error => {
          console.log(error)
          this.makeToast(this.$gettext('Error'), this.$gettext('The server has thrown a timeout error and the file won\'t be downloaded. Please, select a shorter date range and try again'), 'danger')
        })
    },
    /**
     * Makes a GET request to an URL then shows the response in a Toast.
     * Intended to be used within generate API endpoints.
     * @param  {Object} data Should contain `url` and `params` for the request
     */
    generateAsync(data) {
      this.$http.get(data.url, { params: data.params })
        .then(response => {
          const h = this.$createElement
          const mssg = h(
            'p',
            { class: ['text-left', 'mb-0'] },
            [
              'Status: ' + response.status,
              h('pre', {}, JSON.stringify(response.body, undefined, 4))
            ]
          )
          this.makeToast(this.$gettext('Notice'), mssg, 'info')
        }, error => {
          console.log(error)
          this.makeToast(this.$gettext('Warning'), this.$gettext('The server has thrown a timeout error but the CSV file should be uploaded to S3 anyway in a couple minutes'), 'warning')
        })
    },
    toCsv(array) {
      return Papa.unparse(array)
    },
    /**
     * Auto download the data passed
     * @param  {Object} data Should contain the `data` itself, `type` and `filename` for the download
     * @param  {String}  data                  The data itself
     * @param  {String}  type                  The file type, ie.: text/csv
     * @param  {String}  filename              The name of the file to be downloaded
     * @public
     */
    download(data, type, filename) {
      let blob = new Blob([data], { type: type })
      if (navigator.msSaveBlob) { // IE 10+
        navigator.msSaveBlob(blob, filename)
      } else {
        let link = document.createElement('a')
        if (link.download !== undefined) { // feature detection
          // Browsers that support HTML5 download attribute
          let url = URL.createObjectURL(blob)
          link.setAttribute('href', url)
          link.setAttribute('download', filename)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
    },
    localizeDates(str) {
      return str.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/g, (str, p1) => { return this.moment.utc(p1).tz(this.$tz).format('YYYY-MM-DD HH:mm:ss') })
    },
    stringToColour(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
      }
      let colour = '#'
      for (let n = 0; n < 3; n++) {
        let value = (hash >> (n * 8)) & 0xFF
        colour += ('00' + value.toString(16)).substr(-2)
      }
      return colour
    },
    textContrastColor(str) {
      let c = str.substring(1) // strip #
      let rgb = parseInt(c, 16) // convert rrggbb to decimal
      let r = (rgb >> 16) & 0xff // extract red
      let g = (rgb >> 8) & 0xff // extract green
      let b = (rgb >> 0) & 0xff // extract blue

      let luma = 0.2126 * r + 0.7152 * g + 0.0722 * b // per ITU-R BT.709

      if (luma < 128) {
        return 'color: #fff'
      } else {
        return 'color: #000'
      }
    },
    /**
     * Flexible color manipulation on the fly
     * Explanation https://stackoverflow.com/a/13542669/3783511
     */
    pSBC(p, c0, c1, l) {
      let r
      let g
      let b
      let P
      let f
      let t
      let h
      let i = parseInt
      let m = Math.round
      let a = typeof (c1) === 'string'
      if (typeof (p) !== 'number' || p < -1 || p > 1 || typeof (c0) !== 'string' || (c0[0] !== 'r' && c0[0] !== '#') || (c1 && !a)) return null
      if (!this.pSBCr) {
        this.pSBCr = (d) => {
          let n = d.length
          let x = {}
          if (n > 9) {
            [r, g, b, a] = d = d.split(',')
            n = d.length
            if (n < 3 || n > 4) return null
            x.r = i(r[3] === 'a' ? r.slice(5) : r.slice(4))
            x.g = i(g)
            x.b = i(b)
            x.a = a ? parseFloat(a) : -1
          } else {
            if (n === 8 || n === 6 || n < 4) return null
            if (n < 6) d = '#' + d[1] + d[1] + d[2] + d[2] + d[3] + d[3] + (n > 4 ? d[4] + d[4] : '')
            d = i(d.slice(1), 16)
            if (n === 9 || n === 5) {
              x.r = d >> 24 & 255
              x.g = d >> 16 & 255
              x.b = d >> 8 & 255
              x.a = m((d & 255) / 0.255) / 1000
            } else {
              x.r = d >> 16
              x.g = d >> 8 & 255
              x.b = d & 255
              x.a = -1
            }
          }
          return x
        }
      }
      h = c0.length > 9
      h = a ? c1.length > 9 ? true : c1 === 'c' ? !h : false : h
      f = this.pSBCr(c0)
      P = p < 0
      t = c1 && c1 !== 'c' ? this.pSBCr(c1) : P ? {
        r: 0,
        g: 0,
        b: 0,
        a: -1
      } : {
        r: 255,
        g: 255,
        b: 255,
        a: -1
      }
      p = P ? p * -1 : p
      P = 1 - p
      if (!f || !t) return null
      if (l) {
        r = m(P * f.r + p * t.r)
        g = m(P * f.g + p * t.g)
        b = m(P * f.b + p * t.b)
      } else {
        r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)
        g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)
        b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5)
      }
      a = f.a
      t = t.a
      f = a >= 0 || t >= 0
      a = f ? a < 0 ? t : t < 0 ? a : a * P + t * p : 0
      if (h) return 'rgb' + (f ? 'a(' : '(') + r + ',' + g + ',' + b + (f ? ',' + m(a * 1000) / 1000 : '') + ')'
      else return '#' + (4294967296 + r * 16777216 + g * 65536 + b * 256 + (f ? m(a * 255) : 0)).toString(16).slice(1, f ? undefined : -2)
    },
    checkMapProviders() {
      this.$http.get(this.tileProviders.find(x => x.name === 'CartoDB').testTile)
        .then(() => {
          this.tileProviders[this.tileProviders.findIndex(x => x.name === 'CartoDB')].visible = true
          this.tileProvidersReady = true
        }, e => {
          console.log(e)
          this.$root.$children[0].$refs.simplert.justCloseSimplert()
          this.$http.get(this.tileProviders.find(x => x.name === 'Mapbox').testTile)
            .then(() => {
              this.tileProviders[this.tileProviders.findIndex(x => x.name === 'Mapbox')].visible = true
              this.tileProvidersReady = true
            }, e => {
              this.$root.$children[0].$refs.simplert.justCloseSimplert()
              console.log(e)
              this.tileProviders[this.tileProviders.findIndex(x => x.name === 'OpenStreetMap')].visible = true
              this.tileProvidersReady = true
            })
        })
    },
    geoDistance(point1, point2) {
      let lat1 = point1.lat
      let lon1 = point1.lng

      let lat2 = point2.lat
      let lon2 = point2.lng

      let R = 6371e3 // metres
      let φ1 = this.toRadians(lat1)
      let φ2 = this.toRadians(lat2)
      let Δφ = this.toRadians((lat2 - lat1))
      let Δλ = this.toRadians((lon2 - lon1))

      let a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      let d = R * c
      return d
    },
    toRadians(number) {
      return number * Math.PI / 180
    },
    getDistance(point1, point2) {
      return parseInt(this.geoDistance(point1, point2))
    },
    distance(meters, abbr = false) {
      meters = meters * this.distanceCoefficient
      let translate
      if (meters < 1000) {
        translate = !abbr ? this.$gettext('%{meters} meters') : this.$gettext('%{meters}mt')
        return this.$gettextInterpolate(translate, {meters: meters})
      } else {
        translate = !abbr ? this.$gettext('%{meters} kilometers') : this.$gettext('%{meters}km')
        return this.$gettextInterpolate(translate, {meters: (meters / 1000).toFixed(1)})
      }
    },
    getSpeed(item1, item2) {
      return (this.geoDistance(item1, item2) / this.moment.utc(item2.created_at).diff(this.moment.utc(item1.created_at), 'seconds', true)) * 3.6
    },
    snakeCaseToTitleCase(str) {
      return str.replace(/_/g, ' ').replace(
        /\w\S*/g,
        function (txt) {
          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        }
      )
    },
    camelCaseToTitleCase(str) {
      const result = str.replace(/([A-Z])/g, ' $1')
      return (result.charAt(0).toUpperCase() + result.slice(1)).trim()
    },
    openMapOn(placeString) {
      window.open('https://www.google.com/maps/place/' + placeString.replace(/#/g, '%23 ').replace(/\+/g, '%2B'))
    },
    closeModal(modalRef) {
      this.$refs[modalRef].hide()
    },
    refreshTable(tableRef) {
      this.$root.$emit('bv::refresh::table', tableRef)
    },
    accountingNoteCreated(modalId) {
      if (!this.isAccountApprover) {
        this.makeToast(this.$gettext('Notice'), this.$gettext('The accounting note has been created and is pending for review'), 'info', false, 5000)
      }
      this.closeModal(modalId)
    },
    /**
     * Deep merge two objects recursively
     * Arrays are replaced, not merged
     * Null values in source override target values
     * @param  {Object} target The target object (will be cloned, not mutated)
     * @param  {Object} source The source object to merge into target
     * @return {Object}        A new object with merged properties
     * @example
     * const obj1 = { a: 1, b: { c: 2, d: 3 }, e: [1, 2] }
     * const obj2 = { b: { d: 4, f: 5 }, e: [3, 4, 5], g: 6 }
     * deepMerge(obj1, obj2)
     * // Returns: { a: 1, b: { c: 2, d: 4, f: 5 }, e: [3, 4, 5], g: 6 }
     */
    deepMerge(target, source) {
      // Clone target to avoid mutation
      const output = { ...target }

      // Check if both are objects
      if (this.isPlainObject(target) && this.isPlainObject(source)) {
        Object.keys(source).forEach(key => {
          if (this.isPlainObject(source[key])) {
            // If key doesn't exist in target, add it
            if (!(key in target)) {
              Object.assign(output, { [key]: source[key] })
            } else {
              // Recursively merge nested objects
              output[key] = this.deepMerge(target[key], source[key])
            }
          } else {
            // For primitives, arrays, null, etc., replace the value
            Object.assign(output, { [key]: source[key] })
          }
        })
      }

      return output
    },
    /**
     * Check if a value is a plain object (not array, not null, not Date, etc.)
     * @param  {*} item The item to check
     * @return {Boolean} True if item is a plain object
     */
    isPlainObject(item) {
      return item !== null &&
             typeof item === 'object' &&
             !Array.isArray(item) &&
             !(item instanceof Date) &&
             !(item instanceof RegExp)
    },
    /**
     * Initialize Intercom with user profile data
     */
    initializeIntercom() {
      try {
        if (!this.$appConfig.intercom || !this.$appConfig.intercom.appId) {
          console.warn('Intercom AppId not configured')
          return
        }

        // Only enable Intercom for ecosystem id 1
        const ecosystemId = Number(this.eid || this.$store.getters.eid || 0)
        if (ecosystemId !== 1) {
          return
        }

        const profile = this.$store.getters.profile
        const userId = `${profile.id}`
        console.log(profile)
        const hash = CryptoES.algo.HMAC.create(CryptoES.algo.SHA256, this.$appConfig.intercom.appId).update(userId).finalize().toString(CryptoES.enc.Hex)

        const intercomPayload = {
          app_id:"xm93eaw3",
          user_id: userId,
          name: profile.name,
          avatar: profile.profile_picture,
          email: profile.email,
          Pais: profile.company?.country_iso_code || profile.country_iso_code,
          user_hash: hash,
          'Tipo de usuario': 'SaaS'
        }

        // Add company info if it exists
        if (profile.company) {
          intercomPayload.company = {
            id: `${profile.company.id}`,
            name: profile.company.name,
            Pais: profile.company.country_iso_code
          }
        }

        Intercom(intercomPayload)
        console.log('Intercom initialized successfully')
      } catch (error) {
        console.error('Error initializing Intercom:', error)
      }
    }
  }
}
